import { queryKeys } from '@query/keys'
import { usePaginatedQuery } from '@query/usePaginatedQuery'
import React, { useCallback, useContext, useEffect, useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'
import { AuthSessionContext } from '@context/AuthContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { PageSection } from '@ui/components/PageSection'
import { Pagination } from '@ui/v2/Pagination/Pagination'

import { PageQuery, SortOrder, useApiClient } from '@utils/ApiClient'
import { ClientApiClient, emptyPaginationResponse } from '@utils/clientApi'
import { formatDate } from '@utils/date.utils'
import { useDebouncedValue } from '@utils/debounce'
import { useWindowResize } from '@utils/domUtils'

import { ClientIbContestCampaignDto } from '@model/ClientIbContestCampaignDto'
import { FilterQueryProps } from '@model/FilterFields'
import { NameDto } from '@model/NameDto'

import { useGenerateDocumentFile } from '@hooks/useGenerateDocumentFile'
import {
  ScrollToIds,
  useScrollAfterLoad,
  useScrollIntoViewOnPagingEntriesChange,
} from '@hooks/useScrollToElementIds'
import { useScrollToTop } from '@hooks/useScrollToTop'

import { IBContestBadgeMap } from '@shared/components/StatusBadge/StatusBadge'

import { IBContestResultsPage } from './IBContestResultsPage'
import { IBContestTable } from './IBContestTable'

export type IBContestSort = 'name' | 'start_date' | 'end_date' | 'status'

const initialQuery = {}

const useReportsGenerateDocumentFile = (data: ClientIbContestCampaignDto[]) => {
  const { t } = useTranslation()
  const [auth] = useContext(AuthSessionContext)
  const dateFormat = auth?.dateFormatType?.name

  const tableBody = () => {
    const headerCsvData = [
      [
        t('Campaigns.Campaign name'),
        t('Campaigns.Start Date'),
        t('Campaigns.End Date'),
        t('Campaigns.Status'),
      ],
    ]

    return data.reduce((previousValue, currentValue) => {
      return previousValue.concat([
        [
          currentValue.name + '',
          formatDate(currentValue.from, { formatType: dateFormat, withTime: false }),
          formatDate(currentValue.to, { formatType: dateFormat, withTime: false }),
          t(IBContestBadgeMap[currentValue.status.id].label),
        ],
      ])
    }, headerCsvData)
  }

  return useGenerateDocumentFile('ib_contest-campaign', tableBody())
}

const useSelectCampaign = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const [selectedCampaign, setSelectedCampaign] = useState<ClientIbContestCampaignDto | undefined>()

  const handleSelectCampaign = useCallback((campaign: ClientIbContestCampaignDto) => {
    setSelectedCampaign(campaign)
  }, [])

  const handleGoBack = () => {
    const params = new URLSearchParams(location.search)
    params.delete('contestCampaignId')
    const newUrl = `${location.pathname}?${params.toString()}`
    navigate(newUrl, { replace: true })

    setSelectedCampaign(undefined)
  }

  return { selectedCampaign, handleSelectCampaign, handleGoBack }
}

interface IbContestCampaignsFetchProps {
  search: SearchResponseProps
}

const useIbContestCampaignsFetch = (props: IbContestCampaignsFetchProps) => {
  const { search } = props

  const locale = useSessionLanguage()
  const apiClient = useApiClient(ClientApiClient)
  const { account } = useAccountReadContext()

  const query = usePaginatedQuery({
    queryKey: queryKeys.ib.contestCampaigns(
      {
        ...search.search,
        textSearch: search.debouncedSearchTerm,
        sort: search.pageQuery?.sort,
        sortOrder: search.pageQuery?.sortOrder,
      },
      locale
    ),
    queryFn: (pageQuery) => {
      if (account?.id) {
        return apiClient.getIbContestCampaigns({
          ...pageQuery,
          ...search.pageQuery,
          search: search.search,
          textSearch: search.debouncedSearchTerm,
          caller: 'ca',
          languageId: locale,
        })
      }
      return Promise.resolve(emptyPaginationResponse)
    },
    enabled: !!account?.id,
  })

  return { query, forceRefresh: query.forceRefresh }
}

interface SearchResponseProps {
  search: FilterQueryProps
  setSearch(value: FilterQueryProps): void
  searchTerm: string | null
  setSearchTerm: React.Dispatch<React.SetStateAction<string | null>>
  debouncedSearchTerm: string | null
  pageQuery: PageQuery | undefined
  setPageQuery(value: PageQuery): void
}

const useSearch = (props: SortResponseProps): SearchResponseProps => {
  const { sort, sortOrder } = props

  const location = useLocation()

  const [search, setSearch] = useState<FilterQueryProps>(initialQuery)
  const [searchTerm, setSearchTerm] = useState<string | null>(null)
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500)
  const [pageQuery, setPageQuery] = useState<PageQuery>({ sort, sortOrder })

  useEffect(() => {
    setSearch(initialQuery)
  }, [location.pathname])

  return {
    search,
    setSearch,
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    pageQuery,
    setPageQuery,
  }
}

interface SortResponseProps {
  getSortName(): string | undefined
  setSort(value: IBContestSort): void
  sort: IBContestSort | undefined
  sortOrder: SortOrder | undefined
  setSortOrder(value: SortOrder): void
  sortOptions: NameDto<string>[]
}

const useSort = (): SortResponseProps => {
  const { t } = useTranslation()

  const [sort, setSort] = useState<IBContestSort>()
  const [sortOrder, setSortOrder] = useState<SortOrder>()

  const sortOptions = [
    { id: 'Name', name: t('Campaigns.Campaign name') },
    { id: 'From', name: t('Campaigns.Start Date') },
    { id: 'To', name: t('Campaigns.End Date') },
    { id: 'Status.Id', name: t('Campaigns.Status') },
  ]

  const getSortName = () => {
    return sortOptions.find((x) => x.id === sort)?.name
  }

  return {
    getSortName,
    setSort,
    sort,
    sortOrder,
    setSortOrder,
    sortOptions,
  }
}

export const IBContestPage = (): JSX.Element => {
  useScrollToTop()
  const isMobile = useWindowResize()
  const location = useLocation()

  const { selectedCampaign, handleSelectCampaign, handleGoBack } = useSelectCampaign()

  const { t } = useTranslation()
  const sort = useSort()
  const search = useSearch(sort)
  const ibContestCampaigns = useIbContestCampaignsFetch({ search })

  const generatedDocumentFile = useReportsGenerateDocumentFile(ibContestCampaigns.query.data)

  const [isPaginationEntrySelected, setIsPaginationEntrySelected] = useState(false)
  useScrollIntoViewOnPagingEntriesChange(
    ScrollToIds.IBContestHeader,
    isPaginationEntrySelected,
    ibContestCampaigns.query.isLoading,
    setIsPaginationEntrySelected
  )
  useScrollAfterLoad(
    ScrollToIds.IBContestHeader,
    ibContestCampaigns.query.isLoading,
    ibContestCampaigns.query.meta?.pageSize
  )

  useEffect(() => {
    if (search.debouncedSearchTerm !== null) {
      ibContestCampaigns.query.setPageQuery!((oldPageQuery: PageQuery) => ({
        ...oldPageQuery,
        pageIndex: 1,
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.debouncedSearchTerm])

  useEffect(() => {
    if (location.search && ibContestCampaigns.query.data) {
      const contestCampaignId = new URLSearchParams(location.search).get('contestCampaignId')

      const selectedContestCampaignId =
        contestCampaignId !== null &&
        ibContestCampaigns.query.data.find((campaign) => campaign.id === contestCampaignId)

      if (selectedContestCampaignId) {
        handleSelectCampaign(selectedContestCampaignId)
      }
    }
  }, [location.search, ibContestCampaigns.query.data, handleSelectCampaign])

  const handlePageChange = (pageIndex: number, pageSize?: number) => {
    if (pageSize && pageSize !== ibContestCampaigns.query.meta?.pageSize) {
      setIsPaginationEntrySelected(true)
    }
    ibContestCampaigns.query.setPageQuery!({
      ...ibContestCampaigns.query.pageQuery,
      pageIndex,
      pageSize,
    })
  }

  const table = (
    <>
      <IBContestTable
        pageQuery={search.pageQuery}
        setPageQuery={search.setPageQuery}
        tableQuery={ibContestCampaigns.query}
        setSelectedCampaign={handleSelectCampaign}
      />
      {ibContestCampaigns.query.meta && (
        <Pagination
          scrollToHeaderId={ScrollToIds.IBContestHeader}
          maxPageSize={100}
          pageData={ibContestCampaigns.query.meta}
          isLoading={ibContestCampaigns.query.isLoading}
          onPageChanged={handlePageChange}
        />
      )}
    </>
  )

  return selectedCampaign ? (
    <IBContestResultsPage campaign={selectedCampaign} onGoBack={handleGoBack} />
  ) : (
    <div>
      <PageSection
        title={t('Campaigns.Campaigns')}
        id={ScrollToIds.IBContestHeader}
        documentFile={generatedDocumentFile}
        searchField={{
          label: isMobile ? t('Campaigns.Campaign name') : t('Campaigns.Search by Campaign name'),
          onChange: search.setSearchTerm,
          value: search.searchTerm,
          renderResult: () => table,
          canClear: true,
        }}
      />
      {table}
    </div>
  )
}
